import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  MoreVertOutlined as MoreVertIcon,
  SendOutlined as SendIcon,
} from '@mui/icons-material'
import { CircularProgress, Box, useTheme } from '@mui/material'
import { Menu, useGenerateMessage } from '@components'
import { useChatStore } from '@client/store'
import { PyrenzBlueButton } from '@client/theme'
import { memoryOptimizer } from '@client/utility'

const MAX_CHAR_LIMIT = 1500
const MAX_TEXT_AREA_HEIGHT = 200

interface ChatInputProps {
  className?: string
  chat_uuid: string
}

export function ChatInput({ className, chat_uuid }: ChatInputProps) {
  const theme = useTheme()
  const [message, setMessage] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { user, character, setIsGenerating, isGenerating } = useChatStore()
  const { generateMessage, stopStream } = useGenerateMessage()

  const sendMessage = useCallback(async () => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage || !user || !chat_uuid || !character) return
    setMessage('')
    setIsGenerating(true)
    try {
      await generateMessage(trimmedMessage, chat_uuid, 1, 'Generate')
      memoryOptimizer.optimize()
    } finally {
      setIsGenerating(false)
    }
  }, [message, user, chat_uuid, character, setIsGenerating, generateMessage])

  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value),
    []
  )

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        MAX_TEXT_AREA_HEIGHT
      )}px`
    }
  }, [message])

  return (
    <>
      <Box
        className={className}
        sx={{
          width: '100%',
          maxWidth: 960,
          mx: 'auto',
          px: 2,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            width: '100%',
            bgcolor: theme.palette.background.paper + 'cc',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <PyrenzBlueButton
            onClick={() => setIsMenuOpen(true)}
            sx={{
              background: 'none',
              border: 'none',
              boxShadow: 'none',
              minWidth: 'auto',
              p: 1,
              borderRadius: 1.5,
              height: 36,
              flexShrink: 0,
            }}
          >
            <MoreVertIcon fontSize="small" sx={{ color: theme.palette.text.primary }} />
          </PyrenzBlueButton>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                sendMessage()
              }
            }}
            placeholder="Write something..."
            rows={1}
            maxLength={MAX_CHAR_LIMIT}
            disabled={isGenerating}
            style={{ maxHeight: `${MAX_TEXT_AREA_HEIGHT}px` }}
            className="flex-1 min-w-0 bg-transparent outline-none text-white px-3 py-2 resize-none overflow-auto"
          />

          <PyrenzBlueButton
            onClick={isGenerating ? stopStream : sendMessage}
            disabled={!message.trim() && !isGenerating}
            sx={{
              background: 'none',
              border: 'none',
              boxShadow: 'none',
              minWidth: 'auto',
              p: 1,
              borderRadius: '50%',
              ml: 0.5,
              height: 36,
              width: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isGenerating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon fontSize="small" sx={{ color: theme.palette.text.primary }} />
            )}
          </PyrenzBlueButton>
        </Box>
      </Box>

      {isMenuOpen && character && <Menu onClose={() => setIsMenuOpen(false)} char={character} />}
    </>
  )
}